<template>
  <div class="main-custom">
    <Header />
    <section id="printable" class="container">
      <b-loading isFullPage :active="isLoading" />
      <div class="content">
        <span class="title is-3 has-text-color-7 has-text-weight-semibold">{{
          General.SUBSCRIPTION_REQUEST
        }}</span>

        <div v-if="isMobile" class="actions-mobile">
          <b-tooltip
            :label="General.MESSAGES.ALERT_EXTERNAL_SIGNATURE"
            multilined
            :type="isContrast ? 'is-white-dark' : 'is-blue-oil'"
            size="is-large"
            position="is-top"
            :active="!getExternalSignatureDetails.urlFileToView"
          >
            <b-button
              class="has-text-weight-semibold"
              :type="isContrast ? 'is-white' : 'is-green-lawn'"
              :disabled="!getExternalSignatureDetails.urlFileToView"
              expanded
              @click="checkIsConfidential(viewDocument)"
              >{{ General.VIEW_DIGITAL_FILE }}</b-button
            >
          </b-tooltip>
          <b-tooltip
            :label="General.MESSAGES.ALERT_EXTERNAL_SIGNATURE"
            multilined
            :type="isContrast ? 'is-white-dark' : 'is-blue-oil'"
            size="is-large"
            position="is-top"
            :active="!getExternalSignatureDetails.urlFile"
          >
            <b-button
              class="has-text-weight-semibold"
              :type="isContrast ? 'is-white' : 'is-green-lawn'"
              :disabled="!getExternalSignatureDetails.urlFile"
              expanded
              @click="checkIsConfidential(downloadOriginalFile)"
              >{{ General.DOWNLOAD_PRINTABLE_VERSION }}</b-button
            >
          </b-tooltip>
          <b-tooltip
            multilined
            :type="isContrast ? 'is-white-dark' : 'is-blue-oil'"
            size="is-large"
            position="is-top"
            :active="(fileIsLarge && canSign) || Boolean(cannotSign)"
          >
            <template v-slot:content>
              <span
                v-if="!cannotSign"
                class="has-text-weight-semibold is-flex"
                >{{ App.MenuMain.HELP }}</span
              >
              <p class="has-text-left">
                {{ cannotSign || General.MESSAGES.ALERT_SIGN_OVERSIZED_FILE }}
              </p>
            </template>
            <b-button
              v-if="getStatus !== `REFUSED`"
              class="has-text-weight-semibold"
              :type="!canSign ? `is-disable` : getPrimaryColor"
              :disabled="!canSign || fileIsLarge || Boolean(cannotSign)"
              expanded
              @click="validateSignatureModal()"
              >{{
                getStatus !== `SIGNED`
                  ? General.SIGN
                  : General.TEXTS.SIGNED_DOCUMENT
              }}</b-button
            >
          </b-tooltip>
          <b-tooltip
            multilined
            :type="isContrast ? 'is-white-dark' : 'is-blue-oil'"
            size="is-large"
            position="is-top"
            :active="Boolean(cannotSign)"
          >
            <template v-slot:content>
              <p class="has-text-left">
                {{ cannotSign }}
              </p>
            </template>
            <b-button
              v-if="getStatus !== `SIGNED`"
              class="has-text-weight-semibold is-full"
              :type="!canSign ? `is-disable` : getRefusedColor"
              :disabled="!canSign || Boolean(cannotSign)"
              @click="refuseSignatureModal()"
              >{{
                getStatus !== `REFUSED`
                  ? General.DECLINE
                  : General.TEXTS.REFUSED_SUBSCRIPTION
              }}</b-button
            >
          </b-tooltip>
        </div>

        <div
          class="details-title has-text-weight-semibold has-text-color-8 is-size-4 mt-10 pl-10"
        >
          {{ General.DETAILS }}
        </div>
        <div class="signature-info">
          <div class="infos">
            <div class="has-text-weight-semibold">
              {{ `${General.ASKED_BY}` }}
              <span class="has-text-weight-normal">
                {{
                  `${getExternalSignatureDetails.user_requester} - ${getExternalSignatureDetails.capacity_user_requester}`
                }}
              </span>
            </div>
            <div
              v-if="getExternalSignatureDetails.user"
              class="has-text-weight-semibold"
            >
              {{ `${General.ASKED_TO}` }}
              <span class="has-text-weight-normal">
                {{ `${getExternalSignatureDetails.user.toUpperCase()}` }}
              </span>
            </div>
            <div class="has-text-weight-semibold">
              {{ `${General.DATE}: ` }}
              <span class="has-text-weight-normal">
                {{
                  new Date(getExternalSignatureDetails.date)
                    | dateFormat("DD/MM/YYYY")
                }}
                às
                {{
                  new Date(getExternalSignatureDetails.date)
                    | dateFormat("HH:mm")
                }}
              </span>
            </div>
            <div class="has-text-weight-semibold">
              {{ `${General.DOCUMENT}: ` }}
              <span class="has-text-weight-normal">
                {{
                  `${getExternalSignatureDetails.file_name} - ${getExternalSignatureDetails.document_type}`
                }}
              </span>
            </div>
            <div class="has-text-weight-semibold">
              {{ `${General.NUP}: ` }}
              <span class="has-text-weight-normal">
                {{ applyMaskNUP(getExternalSignatureDetails.nup) }}
              </span>
            </div>
            <div class="has-text-weight-semibold">
              {{ `${General.SUBJECT}: ` }}
              <span class="has-text-weight-normal">
                {{ `${getExternalSignatureDetails.suject}` }}
              </span>
            </div>
            <div v-if="isConfidential" class="has-text-weight-semibold">
              {{ `${General.ACCESS_LEVEL}: ` }}
              <span class="has-text-weight-normal">
                {{ secrecyName }}
              </span>
              <b-tooltip
                :type="isContrast ? 'is-white' : 'is-blue-oil'"
                position="is-top"
                :label="secrecyName"
                class="ml-5"
                role="tooltip"
              >
                <div class="circle-red">
                  <b-icon type="is-white" icon="lock" size="is-small" />
                </div>
              </b-tooltip>
            </div>
            <!-- eslint-disable vue/no-v-html -->
            <div class="custom-signature-tag">
              <b-icon
                icon="information-outline"
                :type="isContrast ? 'is-white' : 'is-blue-oil'"
              />
              <span
                class="has-text-weight-medium"
                v-html="General.TEXTS.DETAIL_SIGNATURE_HELP"
              />
            </div>
          </div>
          <aside v-if="!isMobile" class="actions">
            <b-tooltip
              :label="General.MESSAGES.ALERT_EXTERNAL_SIGNATURE"
              multilined
              :type="isContrast ? 'is-white-dark' : 'is-blue-oil'"
              size="is-large"
              position="is-top"
              :active="!getExternalSignatureDetails.urlFileToView"
            >
              <b-button
                class="has-text-weight-semibold"
                :type="isContrast ? 'is-white' : 'is-green-lawn'"
                :disabled="!getExternalSignatureDetails.urlFileToView"
                expanded
                @click="checkIsConfidential(viewDocument)"
                >{{ General.VIEW_DIGITAL_FILE }}</b-button
              >
            </b-tooltip>
            <b-tooltip
              :label="General.MESSAGES.ALERT_EXTERNAL_SIGNATURE"
              multilined
              :type="isContrast ? 'is-white-dark' : 'is-blue-oil'"
              size="is-large"
              position="is-top"
              :active="!getExternalSignatureDetails.urlFile"
            >
              <b-button
                class="has-text-weight-semibold"
                :type="isContrast ? 'is-white' : 'is-green-lawn'"
                :disabled="!getExternalSignatureDetails.urlFile"
                expanded
                @click="checkIsConfidential(downloadOriginalFile)"
                >{{ General.DOWNLOAD_PRINTABLE_VERSION }}</b-button
              >
            </b-tooltip>
            <b-tooltip
              multilined
              :type="isContrast ? 'is-white-dark' : 'is-blue-oil'"
              size="is-large"
              position="is-top"
              :active="(fileIsLarge && canSign) || Boolean(cannotSign)"
            >
              <template v-slot:content>
                <span
                  v-if="!cannotSign"
                  class="has-text-weight-semibold is-flex"
                  >{{ App.MenuMain.HELP }}</span
                >
                <p class="has-text-left">
                  {{ cannotSign || General.MESSAGES.ALERT_SIGN_OVERSIZED_FILE }}
                </p>
              </template>
              <b-button
                v-if="getStatus !== `REFUSED`"
                class="has-text-weight-semibold"
                :type="!canSign ? `is-disable` : getPrimaryColor"
                :disabled="!canSign || fileIsLarge || Boolean(cannotSign)"
                expanded
                @click="validateSignatureModal()"
                >{{
                  getStatus !== `SIGNED`
                    ? General.SIGN
                    : General.TEXTS.SIGNED_DOCUMENT
                }}</b-button
              >
            </b-tooltip>
            <b-tooltip
              multilined
              :type="isContrast ? 'is-white-dark' : 'is-blue-oil'"
              size="is-large"
              position="is-top"
              :active="Boolean(cannotSign)"
            >
              <template v-slot:content>
                <p class="has-text-left">
                  {{ cannotSign }}
                </p>
              </template>
              <b-button
                v-if="getStatus !== `SIGNED`"
                class="has-text-weight-semibold is-full"
                :type="!canSign ? `is-disable` : getRefusedColor"
                :disabled="!canSign || Boolean(cannotSign)"
                @click="refuseSignatureModal()"
                >{{
                  getStatus !== `REFUSED`
                    ? General.DECLINE
                    : General.TEXTS.REFUSED_SUBSCRIPTION
                }}</b-button
              >
            </b-tooltip>
          </aside>
        </div>
        <div
          class="details-title has-text-weight-semibold has-text-color-8 is-size-4 mt-15 pl-10"
        >
          {{ `${General.SIGNATURE_HISTORY}:` }}
        </div>
        <div class="signature-info">
          <div class="infos">
            <div
              v-for="(history,
              index) in getExternalSignatureDetails.processFileSignHistory"
              :key="index"
            >
              <span>{{ history.user_name.toUpperCase() }} </span>
              <span v-if="history.user_cpf.length > 14"
                >({{ General.CNPJ }}</span
              >
              <span v-else>({{ General.CPF }}</span>
              <span> {{ history.user_cpf }}) </span>
              <span v-if="history.status === `SIGNED`"
                >{{ General.ASSIGN_STATE.SIGNED }} em</span
              >
              <span v-else-if="history.status === `REFUSED`"
                >{{ General.ASSIGN_STATE.REFUSED }} em</span
              >
              <span v-else>{{ General.ASSIGN_STATE.PENDING }} desde</span>
              <span>
                {{ new Date(history.date) | dateFormat("DD/MM/YYYY") }}
                às
                {{ new Date(history.date) | dateFormat("HH:mm") }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
    <Footer :isReduced="true" />
  </div>
</template>

<script>
import Header from "@/layout/Header.vue";
import Footer from "@/layout/Footer.vue";
import ModalExternalSignDocument from "@/views/App/ModalExternalSignDocument";
import ModalDeclineRequest from "@/views/App/ModalDeclineRequest";
import ModalWarning from "@/views/App/ModalWarning.vue";

import { General, App } from "@/utils/constants";
import { NUP, applyMask } from "@/utils/protocols";
import { mapActions, mapGetters, mapMutations } from "vuex";
import { downloadFile } from "@/utils/fileUtility";
import { SECRECY as secrecies } from "@/utils/enumerate";

export default {
  name: "ExternalSignature",
  components: {
    Header,
    Footer
  },
  props: {
    solicitationId: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      General,
      App,
      isLoading: false
    };
  },

  computed: {
    ...mapGetters({
      getExternalSignatureDetails: "getExternalSignatureDetails",
      isMobile: "isMobile",
      isConfidentialCheck: "isConfidentialCheck",
      isContrast: "isContrast"
    }),

    canSign() {
      return this.getExternalSignatureDetails.can_sign;
    },

    cannotSign() {
      return this.getExternalSignatureDetails.cannot_sign_justification;
    },

    getStatus() {
      return this.getExternalSignatureDetails.status;
    },

    fileIsLarge() {
      return this.getExternalSignatureDetails.is_large;
    },

    getPrimaryColor() {
      return this.isContrast ? "is-white" : "is-green-lawn";
    },

    getRefusedColor() {
      return this.isContrast ? "is-white" : "is-orange";
    },

    secrecyName() {
      const secrecyId = this.getExternalSignatureDetails.secrecy;
      return secrecies.find(secrecy => secrecy.id === secrecyId).name || "";
    },

    isConfidential() {
      const secrecyId = this.getExternalSignatureDetails.secrecy;
      return this.isConfidentialCheck(secrecyId);
    }
  },

  async beforeMount() {
    this.isLoading = true;
    const status = await this.validateExternalSignatureRequest(
      this.solicitationId
    );
    if (!status) this.$router.replace("/");
    this.isLoading = false;
  },

  methods: {
    ...mapActions({
      validateExternalSignatureRequest: "validateExternalSignatureRequest",
      externalTokensRequest: "externalTokensRequest"
    }),

    ...mapMutations({
      removeExternalUrl: "removeExternalUrl"
    }),

    viewDocument() {
      window.open(this.getExternalSignatureDetails.urlFileToView, "_blank");
    },

    async validateSignatureModal() {
      this.$buefy.modal.open({
        parent: this,
        component: ModalExternalSignDocument,
        props: {
          hash: this.solicitationId,
          onClose: async () =>
            await this.validateExternalSignatureRequest(this.solicitationId)
        },
        hasModalCard: true,
        trapFocus: true
      });
    },

    async refuseSignatureModal() {
      this.$buefy.modal.open({
        parent: this,
        component: ModalDeclineRequest,
        props: {
          hash: this.solicitationId,
          isExternal: true,
          onClose: async () => {
            const status = await this.validateExternalSignatureRequest(
              this.solicitationId
            );
            if (status && this.isConfidential) this.removeExternalUrl();
          }
        },
        hasModalCard: true,
        trapFocus: true
      });
    },

    applyMaskNUP(value) {
      return applyMask(NUP, value);
    },

    async downloadOriginalFile() {
      const blob = await fetch(
        this.getExternalSignatureDetails.urlFile
      ).then(resp => resp.blob());
      const url = URL.createObjectURL(blob);
      downloadFile({
        url,
        attachment_name: this.getExternalSignatureDetails.file_name
          .replace(/(\.\w+)/, "")
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .replace(/\W/g, "_")
      });
      URL.revokeObjectURL(blob);
    },

    checkIsConfidential(callback = () => null) {
      if (this.isConfidential) {
        this.$buefy.modal.open({
          parent: this,
          component: ModalWarning,
          props: {
            title: `${this.General.WARNING}!`,
            message: this.General.MESSAGES.ALERT_DOCUMENT_SECRECY,
            invertOutline: true,
            confirm: callback,
            buttons: [
              { label: General.YES, isConfirm: true, action: true },
              { label: General.NO, isConfirm: false, action: false }
            ]
          },
          hasModalCard: true,
          trapFocus: true
        });
      } else {
        callback();
      }
    }
  }
};
</script>

<style scoped lang="scss">
@import "@/assets/scss/variables";
.container {
  display: flex;
  flex-direction: column;
  align-self: center;
  width: 90%;
  padding: 15px 0;
  min-height: 400px;
}

.content {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: 50px 0;

  @media screen and (max-width: $tablet) {
    padding: 10px 0 40px;
  }

  .title {
    align-self: flex-start;
    margin-bottom: 50px;
    @media screen and (max-width: $tablet) {
      margin-bottom: 20px;
    }
  }

  .details-header {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    width: 100%;
    background-color: $background-color;
    -webkit-print-color-adjust: exact;
    border-radius: 5px;
    padding: 5px;

    .icons-bar {
      display: flex;
      align-items: center;
      gap: 5px;
    }
  }

  .details-title {
    align-self: flex-start;
    width: 100%;
    background-color: $background-color;
    -webkit-print-color-adjust: exact;
    border-radius: 4px;
  }

  .signature-info {
    display: flex;
    width: 100%;
    padding: 20px 5px 5px;

    @media screen and (max-width: $tablet) {
      flex-direction: column;
      gap: 15px;
    }
    .infos {
      display: flex;
      flex-direction: column;
      flex: 1;
      row-gap: 15px;
    }
    .actions {
      display: flex;
      flex-direction: column;
      row-gap: 20px;

      .button {
        border-radius: 4px;
        -webkit-print-color-adjust: exact;
      }
    }
  }

  .actions-mobile {
    display: flex;
    flex-direction: column;
    width: 100%;
    margin: 15px 0;
    row-gap: 10px;

    .button {
      height: 3em;
      border-radius: 4px;
      -webkit-print-color-adjust: exact;
    }
  }
}

.icons-bar .icon {
  margin-right: 0.75rem;
}

.custom-signature-tag {
  display: flex;
  flex: 1;
  width: 90%;
  padding: 5px 10px;
  border-radius: 5px;
  background-color: $color-27;
  @media screen and (max-width: $tablet) {
    width: 100%;
  }
  span {
    font-weight: 500;
    word-break: break-word;
  }
}
</style>
